import { afterEveryRender, afterNextRender, Component, computed, effect, ElementRef, input, OnDestroy, output, signal, viewChild, viewChildren } from '@angular/core';
import { NavItem } from '../../core/models/ui/profile-nav-item.model';
import { ResponsiveTabsUtilities } from '../../core/utilities/responsive-tabs.utilities';

/**
 * A tab strip that shows as many tabs as the bar can hold and moves the rest into a "More" menu,
 * so a narrow screen never hides a tab without saying so (DEV-974).
 *
 * The split is driven entirely by measurement of the rendered tabs — see ResponsiveTabsUtilities.
 * There is no mobile breakpoint here on purpose: a wide bar fits everything and renders exactly the
 * strip it always did, so the same component covers every width.
 */
@Component({
    selector: 'ms-responsive-tabs',
    standalone: true,
    templateUrl: './responsive-tabs.component.html',
    styleUrl: './responsive-tabs.component.scss',
})
export class ResponsiveTabsComponent implements OnDestroy {

    // Carries each tab's id onto its element so a measurement can be matched back to its nav item
    // without depending on the order the view query happens to return.
    private static readonly TAB_ID_ATTRIBUTE = 'data-tab-id';

    // Separates ids inside the key that tells a changed tab set from a re-emitted identical one.
    private static readonly TAB_KEY_SEPARATOR = '|';

    public navItems = input<NavItem[]>([]);
    public tabChanged = output<string>();

    private readonly barRef = viewChild<ElementRef<HTMLElement>>('bar');
    private readonly moreRef = viewChild<ElementRef<HTMLElement>>('more');
    private readonly tabRefs = viewChildren<ElementRef<HTMLElement>>('tab');

    // Natural width of each tab, keyed by nav item id, read back from the rendered elements. Never
    // estimated from the label: the width depends on the loaded font and the breakpoint's padding.
    private readonly measuredWidths = new Map<string, number>();
    private moreButtonWidth = 0;
    private measuredTabKey = '';
    private resizeObserver?: ResizeObserver;
    private isDestroyed = false;

    // Set when a selection destroys the element the user was on, so focus can follow the tab to
    // wherever the next render puts it.
    private pendingFocusTabId?: string;

    private readonly availableWidth = signal<number>(0);
    // Bumped by each measurement pass that learned something, to re-run the layout below.
    private readonly measurementVersion = signal<number>(0);

    private readonly layout = computed(() => {
        this.measurementVersion();
        return ResponsiveTabsUtilities.split(
            this.navItems() ?? [],
            this.measuredWidths,
            this.moreButtonWidth,
            this.availableWidth()
        );
    });

    public readonly visibleItems = computed<NavItem[]>(() => this.layout().visible);
    public readonly overflowItems = computed<NavItem[]>(() => this.layout().overflow);

    constructor() {
        effect(() => {
            const tabKey = (this.navItems() ?? []).map(item => item.id).join(ResponsiveTabsComponent.TAB_KEY_SEPARATOR);
            if (tabKey === this.measuredTabKey) return;

            // A different set of tabs: drop the measurements so the next render lays all of them out
            // again. Selecting a tab re-emits the same ids and so leaves the cache alone.
            this.measuredTabKey = tabKey;
            this.measuredWidths.clear();
            this.measurementVersion.update(version => version + 1);
        });

        afterNextRender(() => {
            this.observeBar();
            this.remeasureWhenFontsLoad();
        });
        afterEveryRender({ write: () => this.applyPendingFocus(), read: () => this.measure() });
    }

    public ngOnDestroy(): void {
        this.isDestroyed = true;
        this.resizeObserver?.disconnect();
    }

    /** A tab in the strip: its element survives the selection, so focus stays where the user put it. */
    public onTabClick(tabId: string): void {
        this.tabChanged.emit(tabId);
    }

    /**
     * A tab in the overflow menu. Selecting it promotes it into the strip, which destroys the menu
     * button the user is standing on, so focus has to be handed to the strip button that replaces it.
     */
    public onOverflowSelect(tabId: string): void {
        this.pendingFocusTabId = tabId;
        this.tabChanged.emit(tabId);
    }

    /**
     * Arrow/Home/End move along the strip and select as they go, which is the activation model the
     * Bootstrap tab plugin used before this component replaced it.
     */
    public onTabKeydown(event: KeyboardEvent, index: number): void {
        const items = this.visibleItems();
        const nextIndex = ResponsiveTabsUtilities.nextTabIndex(event.key, index, items.length);
        if (nextIndex === undefined) return;

        event.preventDefault();
        this.pendingFocusTabId = items[nextIndex].id;
        this.tabChanged.emit(items[nextIndex].id);
    }

    private observeBar(): void {
        const bar = this.barRef()?.nativeElement;
        if (!bar) return;

        // Seed from the first layout instead of waiting on the observer's opening callback, so the
        // strip settles during the render that created it rather than a frame later.
        this.availableWidth.set(bar.getBoundingClientRect().width);

        // The bar is sized by the card around it, never by the tabs inside it, so reacting to its
        // width cannot feed back into another resize.
        this.resizeObserver = new ResizeObserver(entries => {
            this.availableWidth.set(entries[0]?.contentRect.width ?? 0);
        });
        this.resizeObserver.observe(bar);
    }

    private remeasureWhenFontsLoad(): void {
        // A font swap changes how wide the tabs are without changing the bar, so the observer never
        // fires, nothing schedules a render, and the per-render re-measure below never gets to run —
        // leaving tabs clipped by the strip instead of listed in the menu. Inter loads with
        // `display: swap` (src/index.html), so this is the normal path rather than an edge case.
        document.fonts?.ready.then(() => {
            if (this.isDestroyed) return;

            this.measuredWidths.clear();
            this.moreButtonWidth = 0;
            this.measurementVersion.update(version => version + 1);
        });
    }

    private applyPendingFocus(): void {
        const tabId = this.pendingFocusTabId;
        if (!tabId) return;

        // Give up rather than hold the request open: if the split did not put this tab in the strip,
        // no later render will either, and a stale request would snatch focus back from the user.
        if (!this.visibleItems().some(item => item.id === tabId)) {
            this.pendingFocusTabId = undefined;
            return;
        }

        const button = this.findTabElement(tabId)?.querySelector<HTMLElement>('button');
        if (!button) return;

        this.pendingFocusTabId = undefined;
        button.focus();
    }

    private findTabElement(tabId: string): HTMLElement | undefined {
        return this.tabRefs()
            .map(tabRef => tabRef.nativeElement)
            .find(element => element.getAttribute(ResponsiveTabsComponent.TAB_ID_ATTRIBUTE) === tabId);
    }

    private measure(): void {
        const tabRefs = this.tabRefs();
        if (tabRefs.length === 0) return;

        let hasStaleWidth = false;
        let hasNewWidth = false;

        for (const tabRef of tabRefs) {
            const element = tabRef.nativeElement;
            const id = element.getAttribute(ResponsiveTabsComponent.TAB_ID_ATTRIBUTE);
            if (!id) continue;

            // Zero means the strip is hidden behind a breakpoint (the settings panel renders its
            // desktop column instead). Measure it when it is actually on screen.
            const width = element.getBoundingClientRect().width;
            if (width <= 0) continue;

            const cachedWidth = this.measuredWidths.get(id);
            if (cachedWidth === width) continue;

            // A width that moved is a width that moved for every tab, not just this one: the font
            // finished loading, or the breakpoint swapped the padding. Re-lay the whole set out so
            // the tabs currently in the menu are measured again too, rather than trusting stale
            // numbers for the half of the strip that is not rendered.
            if (cachedWidth !== undefined) hasStaleWidth = true;

            this.measuredWidths.set(id, width);
            hasNewWidth = true;
        }

        const moreElement = this.moreRef()?.nativeElement;
        if (moreElement) {
            const moreWidth = moreElement.getBoundingClientRect().width;
            if (moreWidth > 0 && moreWidth !== this.moreButtonWidth) {
                this.moreButtonWidth = moreWidth;
                hasNewWidth = true;
            }
        }

        if (hasStaleWidth) this.measuredWidths.clear();
        if (hasStaleWidth || hasNewWidth) this.measurementVersion.update(version => version + 1);
    }
}
